/**
 * VidnightBackground — "Neuro-Doodle" tiled texture
 *
 * A WhatsApp-style repeating SVG pattern customized for Vidnight:
 * TMS coils, neural synapses, brain waves, molecular hexagons.
 *
 * Low opacity — barely visible at a glance, insanely nice on close inspection.
 * Fixed attachment creates a parallax layered feel as messages scroll.
 */

const PATTERN = [
  "<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'>",

  // H-Coil loop (top-left, rotated 15deg)
  "<g transform='translate(25,30) rotate(15,0,0)'>",
  "<path d='M-8 0 C-8 -10 8 -10 8 0 C8 10 -8 10 -8 0' fill='none' stroke='%239CA3AF' stroke-width='1.6'/>",
  "<path d='M-4 -6 C-4 -12 4 -12 4 -6' fill='none' stroke='%239CA3AF' stroke-width='1.2'/>",
  "</g>",

  // Neural synapse (top-right)
  "<g transform='translate(155,25)'>",
  "<circle cx='0' cy='0' r='2.5' fill='none' stroke='%239CA3AF' stroke-width='1.4'/>",
  "<circle cx='18' cy='8' r='1.8' fill='none' stroke='%239CA3AF' stroke-width='1.4'/>",
  "<line x1='2.5' y1='1' x2='16' y2='7' stroke='%239CA3AF' stroke-width='0.8'/>",
  "<circle cx='-12' cy='10' r='1.5' fill='none' stroke='%239CA3AF' stroke-width='1.2'/>",
  "<line x1='-1.5' y1='2' x2='-11' y2='9' stroke='%239CA3AF' stroke-width='0.6'/>",
  "</g>",

  // Brain wave (center-left)
  "<g transform='translate(15,105) rotate(-5,0,0)'>",
  "<path d='M0 0 Q5 -8 10 0 T20 0 T30 0 T40 0' fill='none' stroke='%239CA3AF' stroke-width='1.2'/>",
  "</g>",

  // Molecular hexagon (center)
  "<g transform='translate(100,95) rotate(30,0,0)'>",
  "<polygon points='0,-8 7,-4 7,4 0,8 -7,4 -7,-4' fill='none' stroke='%239CA3AF' stroke-width='1'/>",
  "<line x1='7' y1='4' x2='14' y2='8' stroke='%239CA3AF' stroke-width='0.8'/>",
  "<line x1='-7' y1='-4' x2='-14' y2='-8' stroke='%239CA3AF' stroke-width='0.8'/>",
  "</g>",

  // TMS coil rings (right side)
  "<g transform='translate(170,110) rotate(-20,0,0)'>",
  "<ellipse cx='0' cy='0' rx='10' ry='6' fill='none' stroke='%239CA3AF' stroke-width='1.2'/>",
  "<ellipse cx='0' cy='0' rx='6' ry='10' fill='none' stroke='%239CA3AF' stroke-width='1'/>",
  "</g>",

  // Neuron body (bottom-left)
  "<g transform='translate(45,170) rotate(10,0,0)'>",
  "<circle cx='0' cy='0' r='4' fill='none' stroke='%239CA3AF' stroke-width='1.4'/>",
  "<line x1='4' y1='0' x2='18' y2='-5' stroke='%239CA3AF' stroke-width='0.8'/>",
  "<line x1='17' y1='-5' x2='22' y2='-8' stroke='%239CA3AF' stroke-width='0.6'/>",
  "<line x1='17' y1='-5' x2='22' y2='-2' stroke='%239CA3AF' stroke-width='0.6'/>",
  "<line x1='-3' y1='-3' x2='-12' y2='-12' stroke='%239CA3AF' stroke-width='0.8'/>",
  "<line x1='-2' y1='3' x2='-10' y2='14' stroke='%239CA3AF' stroke-width='0.8'/>",
  "</g>",

  // Magnetic field arcs (bottom-center)
  "<g transform='translate(115,175) rotate(45,0,0)'>",
  "<path d='M-12 0 A12 12 0 0 1 12 0' fill='none' stroke='%239CA3AF' stroke-width='1'/>",
  "<path d='M-8 0 A8 8 0 0 1 8 0' fill='none' stroke='%239CA3AF' stroke-width='0.8'/>",
  "<path d='M-4 0 A4 4 0 0 1 4 0' fill='none' stroke='%239CA3AF' stroke-width='0.6'/>",
  "</g>",

  // Synapse dots (scattered)
  "<circle cx='75' cy='50' r='1.5' fill='%239CA3AF' opacity='0.7'/>",
  "<circle cx='140' cy='65' r='1.2' fill='%239CA3AF' opacity='0.6'/>",
  "<circle cx='60' cy='140' r='1.8' fill='%239CA3AF' opacity='0.65'/>",
  "<circle cx='180' cy='160' r='1' fill='%239CA3AF' opacity='0.6'/>",

  // Spark / clarity symbol (top-center)
  "<g transform='translate(90,40) rotate(-10,0,0)'>",
  "<line x1='0' y1='-5' x2='0' y2='5' stroke='%239CA3AF' stroke-width='1'/>",
  "<line x1='-4' y1='-3' x2='4' y2='3' stroke='%239CA3AF' stroke-width='0.8'/>",
  "<line x1='4' y1='-3' x2='-4' y2='3' stroke='%239CA3AF' stroke-width='0.8'/>",
  "</g>",

  // Small coil loop (upper-right area)
  "<g transform='translate(175,45) rotate(25,0,0)'>",
  "<path d='M-6 0 C-6 -7 6 -7 6 0 C6 7 -6 7 -6 0' fill='none' stroke='%239CA3AF' stroke-width='1.2'/>",
  "</g>",

  // Brain fold line (mid-right)
  "<g transform='translate(160,140)'>",
  "<path d='M0 0 Q4 -5 8 -2 T16 -4 T24 0' fill='none' stroke='%239CA3AF' stroke-width='1'/>",
  "</g>",

  "</svg>",
].join('');

const bgUrl = `url("data:image/svg+xml,${PATTERN}")`;

export function VidnightBackground() {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-0"
      style={{
        backgroundImage: bgUrl,
        backgroundRepeat: 'repeat',
        backgroundSize: '200px 200px',
        opacity: 0.25,
      }}
    />
  );
}
